import { CSSProperties, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AutoSizer, Grid, List } from "react-virtualized";
import { BrowseDialogViewMode } from "../../app/slices/GlobalSlice";
import PiBrowseListRow from "../../components/PiBrowseListRow";
import PiDropRegion from "../../components/PiDropRegion";
import { AssetDetailsDTO, AssetKey } from "../../dto/AssetDTO";
import useGridColumns from "../../hooks/useGridColumns";
import { useNewAssetUpload } from "../../hooks/UploadNewAssetHook";
import AssetCard from "./AssetCard";
import AssetCardSkeleton from "./AssetCardSkeleton";
import AssetNameField from "./AssetNameField";
import AssetRowIcon from "./AssetRowIcon";
import AssetRowMenu from "./AssetRowMenu";
import FileDropzone from "./FileDropzone";

// Matched to the prop library's tile geometry so the two grids agree.
export const ASSET_TILE_MIN_WIDTH = 210;
export const ASSET_TILE_GAP = 12;
export const ASSET_TILE_ROW_HEIGHT = 192;
export const ASSET_LIST_ROW_HEIGHT = 60;
/** The dashed upload row plus the 12px clearance under it. */
export const ASSET_LIST_DROPZONE_HEIGHT = 88;
export const DEFAULT_ASSET_COLUMNS = 4;
const VERTICAL_SCROLLBAR_ALLOWANCE = 12;

interface AssetResultsProps {
  assets: AssetDetailsDTO[];
  viewMode: BrowseDialogViewMode;
  /** The upload zone leads the results in both views; hidden during search. */
  showDropzone: boolean;
  type?: AssetKey;
  /** Keyboard highlight, indexing ASSETS (never the drop zone). */
  selectedIndex: number;
  onSelectIndex: (index: number) => void;
  onConfirmIndex: (index: number) => void;
  multiSelect: boolean;
  isAssetChecked: (asset: AssetDetailsDTO) => boolean;
  /** Inline rename: at most one asset at a time, owned by the body. */
  renamingAssetId?: string;
  onRenameStart: (asset: AssetDetailsDTO) => void;
  onRenameSubmit: (asset: AssetDetailsDTO, name: string) => void;
  onRenameCancel: () => void;
  /**
   * Ties the results to the search field for assistive tech: the field's
   * `aria-activedescendant` points at one of these option ids.
   */
  listboxId: string;
  optionIdFor: (index: number) => string;
  onColumnsChange: (columns: number) => void;
  onRangeRendered: (
    columns: number,
    rowStartIndex: number,
    rowStopIndex: number
  ) => void;
}

/**
 * The file explorer's results region: virtualised grid or list over the
 * server-paged folder contents, with the upload drop zone as the leading cell
 * of both views — anything appended after a paged list is unreachable in a
 * full folder.
 */
export default function AssetResults(props: AssetResultsProps) {
  const {
    assets,
    viewMode,
    showDropzone,
    type,
    selectedIndex,
    onSelectIndex,
    onConfirmIndex,
    multiSelect,
    isAssetChecked,
    renamingAssetId,
    onRenameStart,
    onRenameSubmit,
    onRenameCancel,
    listboxId,
    optionIdFor,
    onColumnsChange,
    onRangeRendered,
  } = props;
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Same handler the drop zone's file picker uses — it owns the supported-type
  // check and the unsupported-file card.
  const [handleFiles, canUpload] = useNewAssetUpload();

  const offset = showDropzone ? 1 : 0;
  const cellCount = assets.length + offset;

  const measuredColumns = useGridColumns(containerRef, {
    minCellWidth: ASSET_TILE_MIN_WIDTH,
    gap: ASSET_TILE_GAP,
    fallback: DEFAULT_ASSET_COLUMNS,
    max: 6,
  });
  const columns = viewMode === "list" ? 1 : measuredColumns;

  useEffect(() => {
    onColumnsChange(columns);
  }, [columns, onColumnsChange]);

  const tileCell = (cellIndex: number, style: CSSProperties, key: string) => {
    if (cellIndex >= cellCount) return <div key={key} style={style} />;

    if (showDropzone && cellIndex === 0) {
      return (
        <div key={key} style={style} className="p-[6px] assetresults-dropzone">
          <FileDropzone />
        </div>
      );
    }

    const assetIndex = cellIndex - offset;
    const asset = assets[assetIndex];

    return (
      <div key={key} style={style} className="p-[6px]">
        {asset ? (
          <AssetCard
            key={asset.assetId || asset.id}
            asset={asset}
            type={type}
            onSelect={() => onSelectIndex(assetIndex)}
            onConfirm={() => onConfirmIndex(assetIndex)}
            optionId={optionIdFor(assetIndex)}
            isSelected={assetIndex === selectedIndex}
            showCheckbox={multiSelect}
            isChecked={isAssetChecked(asset)}
            isRenaming={renamingAssetId === asset.id}
            onRenameStart={() => onRenameStart(asset)}
            onRenameSubmit={(name) => onRenameSubmit(asset, name)}
            onRenameCancel={onRenameCancel}
          />
        ) : (
          <AssetCardSkeleton />
        )}
      </div>
    );
  };

  const listRow = (rowIndex: number, style: CSSProperties, key: string) => {
    if (showDropzone && rowIndex === 0) {
      return (
        <div key={key} style={style} className="pb-[12px] px-[8px] pt-[2px]">
          <FileDropzone variant="row" />
        </div>
      );
    }

    const assetIndex = rowIndex - offset;
    const asset = assets[assetIndex];

    if (!asset) {
      return (
        <div key={key} style={style} className="py-[2px]">
          <div className="mx-[8px] h-full rounded-[8px] bg-creator-childActive animate-pulse" />
        </div>
      );
    }

    const blocked =
      asset.key === AssetKey.Unsupported ||
      asset.key === AssetKey.Loading ||
      (type !== undefined && asset.key !== type);

    return (
      <div key={key} style={style} className="py-[2px]">
        <PiBrowseListRow
          icon={<AssetRowIcon asset={asset} />}
          title={asset.name}
          titleField={
            renamingAssetId === asset.id ? (
              <AssetNameField
                name={asset.name}
                onSubmit={(name) => onRenameSubmit(asset, name)}
                onCancel={onRenameCancel}
              />
            ) : undefined
          }
          subtitle={asset.updatedBy}
          trailing={
            <>
              {asset.key !== AssetKey.Unsupported &&
                asset.key !== AssetKey.Loading && (
                  <span className="px-[6px] py-[2px] rounded-[4px] border border-creator-border text-[9px] leading-[12px] text-creator-text-sub whitespace-nowrap">
                    {"v" + asset.version.major + "." + asset.version.minor}
                  </span>
                )}
              {multiSelect && !blocked && (
                <span
                  aria-hidden
                  className={
                    "w-[16px] h-[16px] rounded-[4px] border flex items-center justify-center text-[11px] leading-none " +
                    (isAssetChecked(asset)
                      ? "bg-creator-primary border-creator-primary text-white"
                      : "border-creator-border bg-creator-surface")
                  }
                >
                  {isAssetChecked(asset) ? "✓" : ""}
                </span>
              )}
            </>
          }
          menu={<AssetRowMenu asset={asset} onRename={() => onRenameStart(asset)} />}
          optionId={optionIdFor(assetIndex)}
          // Only the type scope dims a row. A failed or in-flight upload is a
          // different state with its own treatment.
          isDisabled={type !== undefined && asset.key !== type}
          isSelected={assetIndex === selectedIndex}
          onSelect={() => !blocked && onSelectIndex(assetIndex)}
          onConfirm={() => !blocked && onConfirmIndex(assetIndex)}
        />
      </div>
    );
  };

  return (
    <PiDropRegion
      onFiles={handleFiles}
      label={t("file_explorer_ui.drop_here")}
      disabled={!canUpload}
      disabledLabel={String(t("file_explorer_ui.upload_needs_folder_title"))}
      className="flex-1 min-h-0 flex flex-col"
      dataTestId="asset-drop-region"
    >
      <div
        ref={containerRef}
        role="listbox"
        aria-label={t("file_explorer_ui.results_label") ?? ""}
        id={listboxId}
      aria-multiselectable={multiSelect}
        className="flex-1 min-h-0 px-[10px] py-[8px]"
        data-testid="asset-view"
      >
        <AutoSizer>
        {({ width, height }) =>
          viewMode === "list" ? (
            <List
              width={width}
              height={height}
              rowCount={cellCount}
              rowHeight={({ index }) =>
                showDropzone && index === 0
                  ? ASSET_LIST_DROPZONE_HEIGHT
                  : ASSET_LIST_ROW_HEIGHT
              }
              scrollToIndex={selectedIndex + offset}
              scrollToAlignment="auto"
              className="pit-quiet-scrollbar"
              rowRenderer={({ index, key, style }) =>
                listRow(index, style, key)
              }
              onRowsRendered={({ startIndex, stopIndex }) =>
                onRangeRendered(1, startIndex, stopIndex)
              }
            />
          ) : (
            <Grid
              width={width}
              height={height}
              columnCount={columns}
              columnWidth={Math.floor(
                (width - VERTICAL_SCROLLBAR_ALLOWANCE) / columns
              )}
              rowCount={Math.ceil(cellCount / columns)}
              rowHeight={ASSET_TILE_ROW_HEIGHT}
              scrollToRow={Math.floor((selectedIndex + offset) / columns)}
              scrollToColumn={(selectedIndex + offset) % columns}
              scrollToAlignment="auto"
              className="pit-quiet-scrollbar"
              cellRenderer={({ columnIndex, key, rowIndex, style }) =>
                tileCell(rowIndex * columns + columnIndex, style, key)
              }
              onSectionRendered={({ rowStartIndex, rowStopIndex }) =>
                onRangeRendered(columns, rowStartIndex, rowStopIndex)
              }
            />
          )
        }
        </AutoSizer>
      </div>
    </PiDropRegion>
  );
}
